// src/components/ContactUsDialog/ContactUsDialog.js

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';

import loKtrLogo from '../../assets/loKtrLogo.png';

const ContactUsDialog = ({ open, onClose }) => {
  const projectId = process.env.REACT_APP_PROJECT_ID || 'loktr0100-demo';
  const POSTMARK_BACKEND_ENDPOINT = `https://australia-southeast1-${projectId}.cloudfunctions.net/sendContactEmail`;

  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStatus(null);
      setFormData({ name: '', email: '', message: '' });
    }, 500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch(POSTMARK_BACKEND_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        console.error('Backend returned an error status:', response.status);
      }
    } catch (error) {
      setStatus('error');
      console.error('Network or fetch error:', error);
    }
    setIsSubmitting(false);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='xs'
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)', // Dims the background by only 20%
          },
        },
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          padding: '8px',
          overflow: 'hidden',
        },
      }}>
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          pt: 2,
        }}>
        Contact loKtr
        <img
          src={loKtrLogo}
          alt='loKtr Logo'
          style={{
            height: '40px',
            objectFit: 'contain',
          }}
        />
      </DialogTitle>
      <DialogContent dividers>
        {status === 'success' ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant='h6' color='success.main' gutterBottom>
              Message Sent Successfully!
            </Typography>
            <Typography>We will get back to you shortly.</Typography>
          </Box>
        ) : (
          <form id='contact-form' onSubmit={handleSubmit}>
            <TextField
              size='small'
              name='name'
              label='Name'
              type='text'
              fullWidth
              required
              value={formData.name}
              onChange={handleChange}
              onKeyDown={handleInputKeyDown}
              sx={{ mb: 2 }}
              slotProps={{ input: { sx: { borderRadius: '12px' } } }}
            />
            <TextField
              size='small'
              name='email'
              label='Email'
              type='email'
              fullWidth
              required
              value={formData.email}
              onChange={handleChange}
              onKeyDown={handleInputKeyDown}
              sx={{ mb: 2 }}
              slotProps={{ input: { sx: { borderRadius: '12px' } } }}
            />
            <TextField
              size='small'
              name='message'
              label='Message'
              multiline
              rows={4}
              fullWidth
              required
              value={formData.message}
              onChange={handleChange}
              slotProps={{ input: { sx: { borderRadius: '12px' } } }}
            />
            {status === 'error' && (
              <Typography color='error' sx={{ mt: 2, textAlign: 'center' }}>
                Something went wrong. Please try again.
              </Typography>
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 2 }}>
        {status !== 'success' && (
          <>
            <Button
              onClick={handleClose}
              color='inherit'
              disabled={isSubmitting}
              sx={{ borderRadius: '20px', px: 3 }}>
              Cancel
            </Button>
            <Button
              type='submit'
              form='contact-form'
              variant='contained'
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#333333',
                color: '#FFFFFF',
                border: '2px solid #FFFFFF',
                borderRadius: '20px',
                px: 3,
                '&:hover': { backgroundColor: '#555555', borderColor: '#FFFFFF' },
              }}>
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Send Message'
              )}
            </Button>
          </>
        )}
        {status === 'success' && (
          <Button
            onClick={handleClose}
            autoFocus
            variant='contained'
            sx={{
              backgroundColor: '#333333',
              color: '#FFFFFF',
              border: '2px solid #FFFFFF',
              borderRadius: '20px',
              px: 3,
              '&:hover': { backgroundColor: '#555555', borderColor: '#FFFFFF' },
            }}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactUsDialog;
